import { format, parseISO } from "date-fns";
import { useState } from "react";
import { Dropdown, Icon } from "semantic-ui-react";
import PageLoader from "../components/PageLoader";
import AgentScatter from "../components/sidekick/dashboard/AgentScatter";
import BusyHoursHeatmap from "../components/sidekick/dashboard/BusyHoursHeatmap";
import CallLengthChart from "../components/sidekick/dashboard/CallLengthChart";
import CallVolumeChart from "../components/sidekick/dashboard/CallVolumeChart";
import DirectionDonut from "../components/sidekick/dashboard/DirectionDonut";
import KpiTiles from "../components/sidekick/dashboard/KpiTiles";
import MinutesUsageCard from "../components/sidekick/dashboard/MinutesUsageCard";
import SentimentDistributionChart from "../components/sidekick/dashboard/SentimentDistributionChart";
import SentimentTrendChart from "../components/sidekick/dashboard/SentimentTrendChart";
import TeamComparisonChart from "../components/sidekick/dashboard/TeamComparisonChart";
import { Notice, Page, PageHeader, Tabs } from "../components/ui";
import { useAuth } from "../contexts/AuthContext";
import { useErrorNotification } from "../hooks/useErrorNotification";
import { useSidekickDashboard } from "../hooks/useSidekickDashboard";
import { useSidekickMinutesUsage } from "../hooks/useSidekickMinutesUsage";
import { colors } from "../style";

const RANGES = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

const SCOPE = {
  organisation: { icon: "building outline", text: (s) => `All of ${s.name}` },
  teams: { icon: "users", text: (s) => `Teams you supervise: ${s.name}` },
  self: {
    icon: "user outline",
    text: (s) => `Your calls (ext ${s.extension})`,
  },
};

const style = {
  scope: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "4px",
    padding: "4px 10px",
    borderRadius: "999px",
    border: `1px solid ${colors.border}`,
    color: colors.textMuted,
    fontSize: "12.5px",
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  row: (columns) => ({
    display: "grid",
    gridTemplateColumns: columns,
    gap: "16px",
    marginTop: "16px",
  }),
};

function Dashboard() {
  const { apiUser } = useAuth();
  const [range, setRange] = useState("30d");
  // Semantic's Dropdown treats "" as no selection, so "all" is the sentinel.
  const [team, setTeam] = useState("all");

  const { data, isLoading, isError, isFetching } = useSidekickDashboard({
    range,
    team: team === "all" ? undefined : team,
  });
  const { data: usage } = useSidekickMinutesUsage();

  useErrorNotification({
    isError,
    title: "Error loading dashboard",
    toastId: "dashboard-error",
    navigateTo: "/",
  });

  if (isLoading || !data) return <PageLoader />;

  const scope = SCOPE[data.scope.type];
  // The server only returns per-person and per-team breakdowns to roles that
  // may see them; the scope check is a second guard so a regular user never
  // renders colleagues' data even if the payload is wrong.
  const canSeeOthers = data.scope.type !== "self";
  const showAgents = canSeeOthers && data.agents?.length > 0;
  const showTeams =
    canSeeOthers && data.team_breakdown?.length > 1 && team === "all";
  const showMinutes = apiUser?.is_admin && usage && data.billing;

  return (
    <Page maxWidth={1440} gap={0}>
      <PageHeader
        title="Dashboard"
        subtitle={
          <div style={style.scope}>
            <Icon name={scope.icon} style={{ margin: 0 }} />
            {scope.text(data.scope)}
            <span style={{ color: colors.textFaint }}>
              · {format(parseISO(data.period.start), "d MMM")} -{" "}
              {format(parseISO(data.period.end), "d MMM yyyy")}
            </span>
          </div>
        }
        actions={
          <div style={style.filters}>
            {data.teams?.length > 0 && (
              <Dropdown
                selection
                compact
                value={team}
                onChange={(_, { value }) => setTeam(value)}
                options={[
                  {
                    key: "all",
                    value: "all",
                    text: apiUser?.is_admin ? "All teams" : "All my teams",
                  },
                  ...data.teams.map((t) => ({
                    key: t.id,
                    value: t.id,
                    text: t.name,
                  })),
                ]}
              />
            )}
            <Tabs
              ariaLabel="Date range"
              value={range}
              onChange={setRange}
              items={RANGES}
            />
          </div>
        }
      />

      <div
        style={{
          marginTop: "20px",
          opacity: isFetching ? 0.6 : 1,
          transition: "opacity 150ms",
        }}
      >
        {data.totals.calls === 0 && (
          <Notice tone="neutral" style={{ marginBottom: "16px" }}>
            There are no recorded calls in this period yet. Try a longer date
            range.
          </Notice>
        )}
        <KpiTiles
          totals={data.totals}
          previous={data.previous_totals}
          daily={data.daily}
        />

        <div style={style.row("minmax(0, 2fr) minmax(0, 1fr)")}>
          <CallVolumeChart daily={data.daily} />
          <DirectionDonut daily={data.daily} />
        </div>

        <div style={style.row("minmax(0, 3fr) minmax(0, 2fr)")}>
          <SentimentTrendChart
            daily={data.daily}
            average={data.totals.avg_sentiment}
          />
          <SentimentDistributionChart sentiment={data.sentiment} />
        </div>

        <div style={style.row("minmax(0, 3fr) minmax(0, 2fr)")}>
          <BusyHoursHeatmap hours={data.hours} />
          <CallLengthChart
            durations={data.durations}
            averageSeconds={data.totals.avg_call_seconds}
          />
        </div>

        {(showAgents || showTeams) && (
          <div
            style={style.row(
              showAgents && showTeams
                ? "minmax(0, 2fr) minmax(0, 1fr)"
                : "minmax(0, 1fr)",
            )}
          >
            {showAgents && (
              <AgentScatter
                agents={data.agents}
                averageSentiment={data.totals.avg_sentiment}
              />
            )}
            {showTeams && <TeamComparisonChart teams={data.team_breakdown} />}
          </div>
        )}

        {showMinutes && (
          <div style={style.row("minmax(0, 1fr)")}>
            <MinutesUsageCard usage={usage} monthly={data.billing.daily} />
          </div>
        )}
      </div>
    </Page>
  );
}

export default Dashboard;
